/**
 * Lens registry — the single place that knows about every lens.
 *
 * Wires each lens type to its renderer constructor and aggregates every
 * lens's examples into one ordered list. The gallery, viewer, and headless
 * scripts all import from here, so adding a lens is a one-file change: import
 * its renderer + examples, add it to RENDERERS and ALL_EXAMPLES.
 */

import type { LensType } from "./types.js";
import type { LensExample, LensRendererCtor } from "./lens.js";

import { ForceRenderer } from "./ForceRenderer.js";
import { FORCE_EXAMPLES } from "./examples.js";

import { SankeyRenderer, SANKEY_EXAMPLES } from "./lenses/sankey.js";
import { TimelineRenderer, TIMELINE_EXAMPLES } from "./lenses/timeline.js";
import { MatrixRenderer, MATRIX_EXAMPLES } from "./lenses/matrix.js";
import { TreeRenderer, TREE_EXAMPLES } from "./lenses/tree.js";
import { RadarRenderer, RADAR_EXAMPLES } from "./lenses/radar.js";

/** Every gallery example, in lens order: force, sankey, timeline, matrix, tree, radar. */
export const ALL_EXAMPLES: LensExample[] = [
  ...FORCE_EXAMPLES,
  ...SANKEY_EXAMPLES,
  ...TIMELINE_EXAMPLES,
  ...MATRIX_EXAMPLES,
  ...TREE_EXAMPLES,
  ...RADAR_EXAMPLES,
];

/**
 * Renderer constructor for each lens type. ForceRenderer takes a wider options
 * object than the shared LensRendererOptions but is structurally compatible
 * with the common surface, so each is cast to LensRendererCtor here.
 */
export const RENDERERS: Record<LensType, LensRendererCtor> = {
  force: ForceRenderer as unknown as LensRendererCtor,
  sankey: SankeyRenderer as unknown as LensRendererCtor,
  timeline: TimelineRenderer as unknown as LensRendererCtor,
  matrix: MatrixRenderer as unknown as LensRendererCtor,
  tree: TreeRenderer as unknown as LensRendererCtor,
  radar: RadarRenderer as unknown as LensRendererCtor,
};

/** Look up an example by id. */
export function exampleById(id: string): LensExample | undefined {
  return ALL_EXAMPLES.find((e) => e.id === id);
}

/** Lens display order for the gallery; only lenses with examples appear. */
const LENS_ORDER: LensType[] = [
  "force",
  "sankey",
  "timeline",
  "matrix",
  "tree",
  "radar",
];

/** Examples grouped by lens, preserving LENS_ORDER and skipping empty lenses. */
export function examplesByLens(): { lens: LensType; examples: LensExample[] }[] {
  return LENS_ORDER.map((lens) => ({
    lens,
    examples: ALL_EXAMPLES.filter((e) => e.lens === lens),
  })).filter((group) => group.examples.length > 0);
}
